import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/Search'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import {
  useTransformsForAdaptations,
  usePropertyAdaptations,
  useAddPropertyAdaptation,
  useDeletePropertyAdaptation,
} from '../hooks/climateAnalytics'
import { useClimateHazards } from '../hooks/analytics'
import { useSnackbar } from 'notistack'
import { HazardInfo } from '../types/climate'
import { ClimateAnalyticsPermissions, PermissionGate, usePermissions } from 'balkerne-core/roles'

function PropertyClimateMitigations() {
  const theme = useTheme()
  const { id: propertyId } = useParams()
  const { data: hazards, isLoading: loadingHazards } = useClimateHazards()
  const [hazard, setHazard] = useState<HazardInfo | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [openStub, setOpenStub] = useState(false)
  const { data: transformsForAdaptations, isLoading: isTransformsLoading } = useTransformsForAdaptations(hazard?.id)
  const { data: assignedAdaptations, isLoading: isAdaptationsLoading } = usePropertyAdaptations(propertyId, hazard?.id)
  const { enqueueSnackbar } = useSnackbar()
  const { possible } = usePermissions()
  console.log('HAZARD:', hazard)
  console.log('assignedAdaptations', assignedAdaptations)

  // Default to first hazard
  useEffect(() => {
    if (!hazard && hazards.length > 0) {
      setHazard(hazards[0])
    }
  }, [hazard, hazards])

  const assignedTransformIds = useMemo(
    () => new Set(assignedAdaptations.map(a => a.transform.id)),
    [assignedAdaptations],
  )

  // Filter transforms by search text and exclude already assigned
  const filteredTransformsForAdaptations = useMemo(() => {
    const unassignedTransformsForAdaptations = transformsForAdaptations.filter(
      transform => !assignedTransformIds.has(transform.id),
    )
    const searchQuery = searchText.trim().toLowerCase()
    if (!searchQuery) return unassignedTransformsForAdaptations
    return unassignedTransformsForAdaptations.filter(transform =>
      [transform.label, transform.description].some(attribute =>
        String(attribute ?? '')
          .toLowerCase()
          .includes(searchQuery),
      ),
    )
  }, [assignedTransformIds, transformsForAdaptations, searchText])

  const { mutate: attachAdaptation, isLoading: attaching } = useAddPropertyAdaptation({
    onSuccess: () => enqueueSnackbar('Adaptation attached', { variant: 'success' }),
  })
  const { mutate: removeAdaptation, isLoading: removing } = useDeletePropertyAdaptation({
    onSuccess: () => enqueueSnackbar('Adaptation removed', { variant: 'info' }),
  })

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100%' }}>
      {/* Sticky header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar + 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'saturate(180%) blur(6px)',
        }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Stack spacing={0.25}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Climate Adaptations
              </Typography>
            </Stack>

            <Box flexGrow={1} />

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="hazard-label">Hazard</InputLabel>
                <Select
                  labelId="hazard-label"
                  value={hazard?.id || -1}
                  label="Hazard"
                  disabled={loadingHazards}
                  onChange={e => {
                    const selected = hazards.find(_hazard => _hazard.id === e.target.value)
                    if (selected) {
                      setHazard(selected)
                    }
                  }}>
                  {hazards.map(_hazard => (
                    <MenuItem key={_hazard.id} value={_hazard.id}>
                      {_hazard.display_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search adaptations"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {/* <PermissionGate action={ClimateAnalyticsPermissions.Modify}>
                <Tooltip title="Create a new adaptation (stub)">
                  <span>
                    <Button variant="outlined" startIcon={<AddIcon />} disabled onClick={() => setOpenStub(true)}>
                      New adaptation
                    </Button>
                  </span>
                </Tooltip>
              </PermissionGate> */}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
        {/* Loading State */}
        {(isTransformsLoading || isAdaptationsLoading) && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          {/* Available transforms */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Available ({filteredTransformsForAdaptations.length})
              </Typography>
            </Stack>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {filteredTransformsForAdaptations.map(transform => {
                return (
                  <>
                    <ListItem key={transform.id} sx={{ alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontWeight: 600 }}>{transform.label}</Typography>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="body2" color="text.secondary">
                            {transform.description}
                          </Typography>
                        }
                      />
                      <Tooltip
                        title={
                          possible(ClimateAnalyticsPermissions.Modify)
                            ? ''
                            : 'You do not have permission to attach adaptations'
                        }>
                        <ListItemSecondaryAction>
                          <Button
                            size="small"
                            disabled={possible(ClimateAnalyticsPermissions.Modify) ? attaching : true}
                            aria-disabled={!possible(ClimateAnalyticsPermissions.Modify)}
                            variant="contained"
                            onClick={() => attachAdaptation({ propertyId, transformId: transform.id })}>
                            Attach
                          </Button>
                        </ListItemSecondaryAction>
                      </Tooltip>
                    </ListItem>
                    <Divider component="li" />
                  </>
                )
              })}
              {filteredTransformsForAdaptations.length === 0 && (
                <ListItem>
                  <ListItemText primary={<Typography color="text.secondary">No adaptations found</Typography>} />
                </ListItem>
              )}
            </List>
          </Box>

          {/* Assigned adaptations */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Assigned ({assignedAdaptations.length})
              </Typography>
            </Stack>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {assignedAdaptations.map(adapt => (
                <>
                  <ListItem key={adapt.id} sx={{ alignItems: 'flex-start' }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography sx={{ fontWeight: 600 }}>{adapt.transform.label}</Typography>
                        </Stack>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {adapt.transform.description}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip
                        title={
                          possible(ClimateAnalyticsPermissions.Modify)
                            ? ''
                            : 'You do not have permission to remove adaptations'
                        }>
                        <span>
                          <IconButton
                            color="error"
                            disabled={possible(ClimateAnalyticsPermissions.Modify) ? removing : true}
                            aria-disabled={!possible(ClimateAnalyticsPermissions.Modify)}
                            onClick={() => removeAdaptation({ propertyId, adaptationId: adapt.id })}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider component="li" />
                </>
              ))}
              {assignedAdaptations.length === 0 && (
                <ListItem>
                  <ListItemText primary={<Typography color="text.secondary">No adaptations assigned</Typography>} />
                </ListItem>
              )}
            </List>
          </Box>
        </Stack>
      </Container>

      {/* Dialog | Create Adaptation  */}
      <Dialog open={openStub} onClose={() => setOpenStub(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create adaptation (stub)</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This is a placeholder. Once the creation API is exposed, this dialog will allow creating organisation-scoped
            adaptation transforms (e.g., multiplicative <code>ymul</code> with a<code>constant_function</code> value
            like 0.8 for 20% reduction).
          </DialogContentText>

          <Stack spacing={2}>
            <FormControl size="small">
              <InputLabel id="stub-hazard">Hazard</InputLabel>
              <Select labelId="stub-hazard" label="Hazard" value={hazard?.id || -1} disabled>
                {hazards.map(_hazard => (
                  <MenuItem key={_hazard.id} value={_hazard.id}>
                    {_hazard.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Slot family"
              size="small"
              value="ymul"
              disabled
              helperText="Adaptations usually use ymul"
            />
            <TextField label="Shape type" size="small" value="constant_function" disabled />
            <TextField label="Value (e.g., 0.8)" size="small" placeholder="0.8" disabled />
            <TextField label="Label" size="small" placeholder="e.g., Flood protection (20% reduction)" disabled />
            <TextField label="Description" size="small" placeholder="Optional" disabled />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStub(false)}>Close</Button>
          <Button variant="contained" disabled>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PropertyClimateMitigations
